/**
 * One party committee: what it was given, and what it did with it.
 *
 * Two halves that are never netted against each other. Money in is the
 * LD-203 record — registered lobbyists certifying twice a year what they
 * gave and to whom. Money out is the FEC's `pas2` — every payment a
 * committee made about a named candidate. They count different dollars over
 * different periods, so each half carries its own window and neither is
 * subtracted from the other.
 *
 * Money out is four categories and there is no total of them anywhere here.
 * The reason is on the page, but briefly: the largest of the four is money
 * spent attacking a candidate, filed by the FEC under the name of the person
 * it attacks. So a candidate list under that heading is a list of people the
 * committee spent money against, and every row says so in words rather than
 * leaving it to the heading — an amount beside a name reads as a gift
 * however the section above it is titled.
 */

import { Link, useParams } from "react-router-dom";
import { ApiError, api, type CommitteeCandidate, type PartyCommitteeDetail } from "../api";
import { useAsync } from "../useAsync";
import { Problem, Spinner } from "../components/bits";
import { PARTY_NAME, TINT, partyKey } from "../components/party";
import {
  EFFECTS,
  EFFECT_ORDER,
  NAMED,
  UNCLASSIFIED_FACTS,
  figure,
  period,
} from "../components/committees";
import { CommitteeProfile } from "../components/WhoTheyAre";

/** What the LD-203 lets a filer name, and what it means for a candidate.
 *  Only the federal account can ever reach one; the other three have their
 *  own far higher limits precisely because they cannot. */
const ACCOUNTS: Record<string, { label: string; note: string }> = {
  federal: {
    label: "Federal account",
    note: "The ordinary account, and the only one this money can be paid to a candidate out of.",
  },
  headquarters: {
    label: "Headquarters account",
    note: "For the committee's own building. Cannot be spent on a candidate.",
  },
  recount: {
    label: "Recount and legal proceedings",
    note: "For contesting a result after it is declared. Cannot be spent on a candidate.",
  },
  convention: {
    label: "Nominating convention",
    note: "For staging the party's convention. Cannot be spent on a candidate.",
  },
};

export function CommitteePage() {
  const { committeeId = "" } = useParams();
  const found = useAsync(() => api.partyCommittee(committeeId), [committeeId]);

  if (found.error) {
    const missing = found.error instanceof ApiError && found.error.status === 404;
    return (
      <div className="mx-auto max-w-2xl px-5 py-16">
        {missing ? (
          <div className="rounded-2xl border border-line bg-card p-10 text-center">
            <p className="font-display text-xl font-semibold">No committee with that id.</p>
            <p className="mt-2 text-muted">
              This holds the party committees that moved money to a candidate in the cycles the
              corpus covers.
            </p>
          </div>
        ) : (
          <Problem error={found.error} retry={found.reload} />
        )}
        <p className="mt-6 text-center">
          <Link to="/money/committees" className="font-medium text-brand hover:underline">
            ← The party committees
          </Link>
        </p>
      </div>
    );
  }
  if (found.loading || !found.data) return <Spinner label="Reading the filings…" />;
  return <Committee data={found.data} />;
}

/** Split from the fetching so a test can render it against a payload. */
export function Committee({ data }: { data: PartyCommitteeDetail }) {
  const named = NAMED[data.name.trim().toUpperCase()];
  const { money_in: moneyIn, money_out: moneyOut } = data;
  const spent = moneyOut.by_effect.filter((row) => row.amount !== 0);

  return (
    <div className="mx-auto max-w-5xl px-5 py-10">
      <p className="text-sm">
        <Link to="/money/committees" className="text-muted hover:text-ink">
          ← The party committees
        </Link>
      </p>

      <h1 className="mt-3 font-display text-3xl font-semibold">{data.name}</h1>
      {named ? (
        <p className="mt-1 text-muted">
          {named.full} — the committee that raises and spends for {named.whose}.
        </p>
      ) : (
        <p className="mt-1 text-muted">
          A party committee registered with the FEC{data.state ? ` in ${data.state}` : ""}.
        </p>
      )}

      {/* The two windows, together, before either figure. The halves are
          meant to be read against each other and they do not cover the same
          ground; a reader who assumes they do will read the difference
          between them as a balance. */}
      <div className="mt-6 grid gap-3 sm:grid-cols-2">
        <div className="rounded-2xl border border-line bg-sunk p-4">
          <p className="text-xs font-semibold uppercase tracking-wide text-faint">Money in</p>
          <p className="mt-1 text-sm">
            Lobbyists' own LD-203 filings, <span className="font-medium">{period(moneyIn.covers)}</span>
          </p>
        </div>
        <div className="rounded-2xl border border-line bg-sunk p-4">
          <p className="text-xs font-semibold uppercase tracking-wide text-faint">Money out</p>
          <p className="mt-1 text-sm">
            FEC committee-to-candidate records,{" "}
            <span className="font-medium">{period(moneyOut.covers)}</span>
          </p>
        </div>
      </div>
      <p className="mt-2 max-w-3xl text-sm text-faint">
        The two halves come from different sources over different periods and count different
        dollars. Neither is subtracted from the other, and the difference between them is not a
        balance.
      </p>

      {/* Who it is, before what it moved. `omitFunders` because the section
          immediately below is the same LD-203 money at full length with the
          name-matching published under it. */}
      <CommitteeProfile committeeId={data.committee_id} omitFunders />

      {/* ---------------------------------------------------------- in --- */}
      <section className="mt-12">
        <h2 className="font-display text-2xl font-semibold">What lobbyists gave it</h2>

        {moneyIn.by_account.length === 0 ? (
          <p className="mt-3 max-w-3xl text-muted">
            No disclosed lobbyist money reaches this committee. Only the four national campaign
            committees are named often enough on an LD-203 to resolve.
          </p>
        ) : (
          <>
            <p className="tnum mt-3 font-display text-4xl font-semibold">{figure(moneyIn.federal)}</p>
            <p className="mt-1 max-w-3xl text-muted">
              to the federal account — the only part of this that could ever become a contribution
              to a candidate, and so the only part that belongs in the same sentence as what the
              committee spent.
            </p>

            <ul className="mt-5 grid gap-3 sm:grid-cols-3">
              {moneyIn.by_account.map((row) => {
                const account = ACCOUNTS[row.account];
                const reachable = row.account === "federal";
                return (
                  <li
                    key={row.account}
                    className={`rounded-2xl border p-5 shadow-sm ${
                      reachable ? "border-brand/40 bg-brand-soft" : "border-line bg-card"
                    }`}
                  >
                    <p className="tnum font-display text-2xl font-semibold">
                      {figure(row.amount)}
                    </p>
                    <p className="mt-0.5 text-sm font-medium">
                      {account?.label ?? row.account}
                    </p>
                    <p className="mt-1.5 text-xs leading-relaxed text-muted">
                      {account?.note ?? "An account named on the filing."}
                    </p>
                    <p className="mt-2 text-xs text-faint">
                      {row.gifts.toLocaleString()} cheques · {row.donors.toLocaleString()} firms
                    </p>
                  </li>
                );
              })}
            </ul>

            {moneyIn.donors.length > 0 && (
              <div className="mt-8">
                <h3 className="font-display text-xl font-semibold">Who funded it</h3>
                <p className="mt-1 max-w-3xl text-sm text-muted">
                  The organisations whose lobbyists disclosed giving to the federal account, largest
                  first. Money to the other accounts is not in this list, because it cannot reach a
                  candidate.
                </p>
                <ol className="mt-3 divide-y divide-line overflow-hidden rounded-2xl border border-line bg-card shadow-sm">
                  {moneyIn.donors.map((donor) => (
                    <li
                      key={`${donor.registrant_id ?? donor.registrant}`}
                      className="flex flex-wrap items-baseline gap-x-4 gap-y-1 px-5 py-3"
                    >
                      <span className="tnum w-20 font-medium">{figure(donor.amount)}</span>
                      <span className="min-w-48 flex-1">
                        {donor.registrant_id === null ? (
                          <span className="font-medium">{donor.registrant}</span>
                        ) : (
                          <Link
                            to={`/money/${donor.registrant_id}`}
                            className="font-medium hover:text-brand"
                          >
                            {donor.registrant}
                          </Link>
                        )}
                      </span>
                      <span className="text-sm text-faint">
                        {donor.gifts.toLocaleString()} cheques · last {donor.latest ?? "—"}
                      </span>
                    </li>
                  ))}
                </ol>
              </div>
            )}

            <Confidence by={moneyIn.by_confidence} />
          </>
        )}
      </section>

      {/* --------------------------------------------------------- out --- */}
      <section className="mt-14">
        <h2 className="font-display text-2xl font-semibold">What it spent, on candidates by name</h2>

        {spent.length === 0 ? (
          <p className="mt-3 max-w-3xl text-muted">
            This committee has not spent anything on a named candidate in the cycles held here.
          </p>
        ) : (
          <>
            <p className="mt-1.5 max-w-3xl text-muted">
              Four figures, not four parts of one. They are four different acts and this page never
              adds them — the largest of them is money spent attacking a candidate, which is not
              money anybody was given.
            </p>

            <ul className="mt-5 grid gap-3 sm:grid-cols-2 xl:grid-cols-4">
              {spent.map((row) => {
                const facts = row.effect ? EFFECTS[row.effect] : UNCLASSIFIED_FACTS;
                const against = facts.direction === "against";
                return (
                  <li
                    key={row.effect ?? "unclassified"}
                    className={`rounded-2xl border p-5 shadow-sm ${
                      against ? "border-gop/40 bg-gop/5" : "border-line bg-card"
                    }`}
                  >
                    <p
                      className={`tnum font-display text-2xl font-semibold ${
                        against ? "text-gop" : ""
                      }`}
                    >
                      {figure(row.amount)}
                    </p>
                    <p className="mt-0.5 text-sm font-medium">{facts.label}</p>
                    <p className="mt-1 text-xs text-faint">
                      {row.transactions.toLocaleString()}{" "}
                      {row.transactions === 1 ? "payment" : "payments"} ·{" "}
                      {row.candidates.toLocaleString()}{" "}
                      {row.candidates === 1 ? "candidate" : "candidates"} ·{" "}
                      {facts.codes.slice(0, 2).join(", ")}
                    </p>
                  </li>
                );
              })}
            </ul>

            <Cycles by={data.money_out.by_cycle} />

            {[...EFFECT_ORDER, ...Object.keys(moneyOut.candidates).filter(
              (key) => !(EFFECT_ORDER as string[]).includes(key),
            )].map((key) => (
              <CandidateList
                key={key}
                effect={key}
                candidates={moneyOut.candidates[key] ?? []}
                spent={moneyOut.by_effect.find((row) => (row.effect ?? "null") === key)?.amount ?? 0}
                of={
                  moneyOut.by_effect.find((row) => (row.effect ?? "null") === key)?.candidates ?? 0
                }
              />
            ))}
          </>
        )}
      </section>
    </div>
  );
}

/**
 * One category, and everyone on the receiving end of it.
 *
 * The heading says what the committee did; every row repeats it, because a
 * reader who lands mid-page or skims the amounts sees rows, not headings.
 */
function CandidateList({
  effect,
  candidates,
  spent,
  of,
}: {
  effect: string;
  candidates: CommitteeCandidate[];
  spent: number;
  /** Every candidate in this category, against the 25 the API serves. The
   *  DCCC reaches ninety-odd in one of these and a list that stopped at
   *  twenty-five without saying so would understate its reach by two
   *  thirds. */
  of: number;
}) {
  if (candidates.length === 0) return null;
  const facts = EFFECT_ORDER.includes(effect as never)
    ? EFFECTS[effect as keyof typeof EFFECTS]
    : UNCLASSIFIED_FACTS;
  const against = facts.direction === "against";
  const strays = candidates.filter((row) => row.bioguide === null).length;

  return (
    <div className="mt-10">
      <div className="flex flex-wrap items-baseline gap-x-3">
        <h3
          className={`font-display text-xl font-semibold ${against ? "text-gop" : ""}`}
        >
          {facts.label}
        </h3>
        <span className="tnum text-sm font-medium text-muted">{figure(spent)}</span>
        <span className="flex gap-1">
          {facts.codes.map((code) => (
            <span
              key={code}
              className="tnum rounded bg-sunk px-1.5 py-0.5 text-xs font-semibold text-muted"
            >
              {code}
            </span>
          ))}
        </span>
      </div>
      <p className="mt-1 max-w-3xl text-sm text-muted">{facts.blurb}</p>

      <ol
        className={`mt-3 divide-y overflow-hidden rounded-2xl border shadow-sm ${
          against ? "divide-gop/15 border-gop/40 bg-gop/5" : "divide-line border-line bg-card"
        }`}
      >
        {candidates.map((row) => (
          <li key={row.candidate_id}>
            <Row row={row} preposition={facts.preposition} against={against} />
          </li>
        ))}
      </ol>

      {of > candidates.length && (
        <p className="mt-2 text-sm text-faint">
          Showing the{" "}
          {candidates.length === 1 ? "largest" : `${candidates.length.toLocaleString()} largest`} of{" "}
          {of.toLocaleString()} candidates in this category.
        </p>
      )}

      {strays > 0 && (
        <p className="mt-2 max-w-3xl text-sm text-faint">
          {strays === candidates.length ? "Every one" : `${strays}`} of these{" "}
          {strays === 1 ? "is a candidate" : "are candidates"} the corpus holds no member record
          for, so {strays === 1 ? "it is" : "they are"} shown under the FEC's own candidate id.
          {against
            ? " That is the ordinary case here: money spent attacking a candidate is aimed at somebody the other party wanted to beat, and most of them lost."
            : " They are mostly challengers who did not win the seat."}
        </p>
      )}
    </div>
  );
}

/**
 * One candidate.
 *
 * `{amount} {preposition} {name}` in that order and in one sentence, so the
 * direction cannot be separated from the figure by a column boundary or by
 * somebody reading fast. A row that said "$10.5m — David McCormick" under
 * any heading at all would be read as money he was given.
 */
function Row({
  row,
  preposition,
  against,
}: {
  row: CommitteeCandidate;
  preposition: string;
  against: boolean;
}) {
  const body = (
    <>
      <span className={`tnum w-20 font-semibold ${against ? "text-gop" : ""}`}>
        {figure(row.amount)}
      </span>
      <span className="min-w-48 flex-1">
        <span className={against ? "font-medium text-gop" : "text-muted"}>{preposition}</span>{" "}
        {row.full_name ? (
          <>
            <span className="font-medium">{row.full_name}</span>
            {row.party && (
              <span className={`ml-2 text-sm font-medium ${TINT[partyKey(row.party)]}`}>
                {PARTY_NAME[partyKey(row.party)]}
              </span>
            )}
            {row.state && <span className="ml-1.5 text-sm text-faint">{row.state}</span>}
          </>
        ) : (
          <>
            <span className="tnum font-medium">{row.candidate_id}</span>
            <span className="ml-2 text-sm text-faint">
              not a member of this corpus, so there is no member page
            </span>
          </>
        )}
      </span>
      <span className="text-sm text-faint">
        {row.transactions.toLocaleString()} {row.transactions === 1 ? "payment" : "payments"}
      </span>
    </>
  );

  return row.bioguide ? (
    <Link
      to={`/member/${row.bioguide}`}
      className="flex flex-wrap items-baseline gap-x-4 gap-y-1 px-5 py-3 transition hover:bg-sunk"
    >
      {body}
    </Link>
  ) : (
    <div className="flex flex-wrap items-baseline gap-x-4 gap-y-1 px-5 py-3">{body}</div>
  );
}

/** Cycle by cycle, still by category. A committee's spending is lumpy — the
 *  DSCC spent $43.8m attacking in 2024 and nothing at all in 2026 so far —
 *  and one figure over three cycles hides which election it belongs to. */
function Cycles({ by }: { by: PartyCommitteeDetail["money_out"]["by_cycle"] }) {
  if (by.length === 0) return null;
  const cycles = [...new Set(by.map((row) => row.cycle))].sort();
  return (
    <div className="mt-8 overflow-x-auto">
      <h3 className="font-display text-xl font-semibold">By election cycle</h3>
      <table className="mt-3 w-full min-w-lg overflow-hidden rounded-2xl border border-line bg-card text-sm shadow-sm">
        <thead>
          <tr className="border-b border-line text-left">
            <th className="px-5 py-2.5 font-medium">Cycle</th>
            {EFFECT_ORDER.map((key) => (
              <th
                key={key}
                className={`px-5 py-2.5 text-right font-medium ${
                  EFFECTS[key].direction === "against" ? "text-gop" : ""
                }`}
              >
                {EFFECTS[key].label}
              </th>
            ))}
          </tr>
        </thead>
        <tbody className="divide-y divide-line">
          {cycles.map((cycle) => (
            <tr key={cycle}>
              <td className="tnum px-5 py-2.5 font-medium">{cycle}</td>
              {EFFECT_ORDER.map((key) => {
                const cell = by.find((row) => row.cycle === cycle && row.effect === key);
                return (
                  <td
                    key={key}
                    className={`tnum px-5 py-2.5 text-right ${
                      EFFECTS[key].direction === "against" ? "text-gop" : "text-muted"
                    }`}
                  >
                    {cell ? figure(cell.amount) : "—"}
                  </td>
                );
              })}
            </tr>
          ))}
        </tbody>
      </table>
      <p className="mt-2 text-sm text-faint">
        Read down a column, not across a row. The four columns are four different things.
      </p>
    </div>
  );
}

/** How the filer's own spelling was resolved to this committee. Published
 *  because an acronym and a spelt-out name are not equally certain, and
 *  rendering them identically hides the one thing a reader would check. */
function Confidence({ by }: { by: Record<string, number> }) {
  const rows = Object.entries(by).filter(([, count]) => count > 0);
  if (rows.length === 0) return null;
  const total = rows.reduce((sum, [, count]) => sum + count, 0);
  return (
    <p className="mt-4 max-w-3xl text-sm text-faint">
      Recipients are free text on an LD-203 and this committee is named{" "}
      {rows
        .map(([how, count]) => `${count.toLocaleString()} times by ${how}`)
        .join(", and ")}{" "}
      — {total.toLocaleString()} filings in all. A name is matched only when every word in it
      belongs to the committee, so “Members of the DSCC” and “Sen. John Thune and NRSC” are refused
      rather than counted here.
    </p>
  );
}
